import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import {
    ProviderConfig,
    STATUS_PROVIDERS,
    STATUS_PROVIDER_ORDER,
    StatusProviderId,
    vippsMobilePayFor,
} from '../models/provider-status.model';

/** What a unit has configured, as far as third-party statuses are concerned. */
export interface UnitPaymentSetup {
    /** Clients/{route}/AppInfo/Context/paymentProvider: 'reepay' | 'stripe' | 'surfboard' (null if not set). */
    paymentProvider: string | null;
    /** Clients/{route}/AppInfo/Context/paymentOptions/{x}/enabled (the Payment Options toggles). */
    swishEnabled: boolean;
    mobilepayEnabled: boolean;
    vippsEnabled: boolean;
}

/**
 * Which providers' statuses a unit sees:
 * - paymentProvider: stripe → Stripe, surfboard → Surfboard, reepay → Frisbii.
 *   Not set (or unknown) → Frisbii (reepay is the default on unit creation).
 * - Payment Options toggles: Swish when enabled; Vipps MobilePay when the Vipps or the MobilePay
 *   toggle is enabled (one company, one status page — see vippsMobilePayFor for the country scope).
 */
export function resolveStatusProviders(setup: UnitPaymentSetup): StatusProviderId[] {
    const ids = new Set<StatusProviderId>();

    switch ((setup.paymentProvider || '').toLowerCase()) {
        case 'stripe': ids.add('stripe'); break;
        case 'surfboard': ids.add('surfboard'); break;
        default: ids.add('frisbii');
    }
    if (setup.swishEnabled) ids.add('swish');
    if (setup.mobilepayEnabled || setup.vippsEnabled) ids.add('vippsmobilepay');

    return STATUS_PROVIDER_ORDER.filter((id) => ids.has(id));
}

@Injectable({
    providedIn: 'root',
})
export class UnitStatusProvidersService {
    constructor(private afDatabase: AngularFireDatabase) {}

    /**
     * The status pages to read for a unit. `countryCode` is the sales location's
     * salesLocationCountryCode (SQL SalesLocation.CountryCode, via AuthService) and picks
     * the Vipps MobilePay country group.
     */
    async getProviders(routeName: string, countryCode: string | null | undefined): Promise<ProviderConfig[]> {
        const ids = resolveStatusProviders(await this.getPaymentSetup(routeName));
        return ids.map((id) => (id === 'vippsmobilepay' ? vippsMobilePayFor(countryCode) : STATUS_PROVIDERS[id]));
    }

    async getPaymentSetup(routeName: string): Promise<UnitPaymentSetup> {
        const context = `Clients/${routeName}/AppInfo/Context`;
        const [paymentProvider, paymentOptions] = await Promise.all([
            this.read(`${context}/paymentProvider`),
            this.read(`${context}/paymentOptions`),
        ]);
        return {
            paymentProvider: typeof paymentProvider === 'string' ? paymentProvider : null,
            swishEnabled: paymentOptions?.swish?.enabled === true,
            mobilepayEnabled: paymentOptions?.mobilepay?.enabled === true,
            vippsEnabled: paymentOptions?.vipps?.enabled === true,
        };
    }

    private async read(path: string): Promise<any> {
        try {
            return (await this.afDatabase.database.ref(path).once('value')).val();
        } catch (err) {
            console.warn(`[UnitStatusProvidersService] Could not read ${path}:`, err);
            return null;
        }
    }
}
